import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import findIndex from 'lodash/findIndex';
import ItemTabs from './components/ItemTabs';
import './styles.scss';

/**
 * Tabs item for mobile experience
 * */
const MobileItemTabs = (props) => {
  const {
    onChange,
    learnosityData: { app, items, currentItem },
  } = props;
  const reference = currentItem.source.reference;
  const itemReferences = Object.keys(items);
  const [currentItemData, setCurrentItemData] = useState({
    reference,
    index: findIndex(itemReferences, (item) => item === reference),
  });
  const [tabContainers, setTabContainers] = useState({
    details: null,
    responses: null,
  });

  const { index: questionIndex, reference: questionReference } =
    currentItemData;
  const loadedCurrentItem = items[questionReference];
  const { response_ids, questions, features } = loadedCurrentItem;

  const passageList =
    (features || []).filter((item) => item.type === 'sharedpassage') || [];
  const hasPassages = passageList.length >= 1;
  const hasMultiPartQuestions = response_ids.length > 1;
  const isMCQ = !!questions.find((item) => item.type === 'mcq');

  useEffect(
    () => () => {
      app.off('item:changing');
      app.off('item:load');
    },
    [reference],
  );

  /**
   * On item change event callback
   * @param {number} itemIndex The current item index
   * */
  const onItemChanging = (itemIndex) => {
    const reference = itemReferences[itemIndex];
    setCurrentItemData({
      reference,
      index: itemIndex,
    });
  };
  app.once('item:changing', onItemChanging);

  /**
   * On item load event callback
   * Used instead of onItemChanging because we need it to fire
   * when the app loads the first item
   * */
  const onItemLoad = () => props.onHighlight(app.getCurrentItem().reference);
  app.once('item:load', onItemLoad);

  const currentItemContainer = document.querySelector(
    `[data-reference="${questionReference}"] .row`,
  );
  if (!currentItemContainer) return null;

  const currentItemChildren = currentItemContainer.children;
  const columnsNr = currentItemChildren.length;

  const setTabContainersByLayout = () => {
    try {
      if (columnsNr === 2) {
        /**
         * When item has by default two columns - use the columns within the menu as containers
         * in this case each container should have a list (dom elements) of passages and responses
         * */
        setTabContainers({
          details: [currentItemChildren[0]],
          responses: [currentItemChildren[1]],
        });
      } else {
        /**
         * 2 columns when:
         * MCQ - passages >= 1
         * FRQ - no multipart and no passage
         * FRQ - multi part and multi passage
         * */

        /**
         * 1 column when:
         * MCQ - no passage
         * FRQ - no passage and multipart
         * */

        const isNotAvailable =
          (isMCQ && !hasPassages) ||
          (!isMCQ && !hasPassages && hasMultiPartQuestions);

        if (!isNotAvailable) {
          let details;
          let responses;
          if (hasPassages) {
            details = features.map(({ feature_id }) => {
              return currentItemContainer.querySelector(
                `[id^="${feature_id}"]`,
              );
            });
            responses = response_ids.map((responseId) => {
              return currentItemContainer.querySelector(
                `[id^="${responseId}"]`,
              );
            });
          } else {
            const questionContainer = currentItemContainer.querySelector(
              `[id^="${questions[0].response_id}"]`,
            );
            details = [questionContainer.querySelector('.lrn_question')];
            responses = [
              questionContainer.querySelector('.lrn_response_wrapper'),
            ];
          }

          setTabContainers({
            details,
            responses,
          });
        }
      }
    } catch (Error) {
      console.log('MobileItemTabs - detecting containers - Error:', Error); // eslint-disable-line
    }
  };

  const handleNodeAddition = (node) => {
    const isLRNWidget =
      node.nodeType === Node.ELEMENT_NODE &&
      node.classList.contains('lrn_widget');
    if (isLRNWidget) {
      setTabContainersByLayout(node);
    }
  };

  /* The LRN question element are not immeditately added to the DOM
    Issue found on FY-35010
  */
  const observeLRNElements = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(handleNodeAddition);
        }
      });
    });

    observer.observe(currentItemContainer, { childList: true, subtree: true });
    const lrnWidgetElement = document.querySelectorAll('.lrn_widget');
    setTabContainersByLayout(lrnWidgetElement);
    return () => observer.disconnect();
  };

  useEffect(() => {
    setTabContainers({
      details: null,
      responses: null,
    });
    observeLRNElements();
  }, [questionIndex]);

  return (
    (tabContainers.details || tabContainers.responses) && (
      <Fragment>
        <ItemTabs
          columnsNr={columnsNr}
          tabContainers={tabContainers}
          questionIndex={questionIndex + 1}
          onChange={onChange}
        />
      </Fragment>
    )
  );
};

MobileItemTabs.propTypes = {
  learnosityData: PropTypes.shape({
    app: PropTypes.object.isRequired,
    items: PropTypes.object.isRequired,
    currentItem: PropTypes.object.isRequired,
  }),
  bottomContainer: PropTypes.any,
  onChange: PropTypes.func,
};

export default MobileItemTabs;
